import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Box,
  CircularProgress,
} from '@mui/material';
import Plot from 'react-plotly.js';
import updateSummaryDataMode from '../utils/summary_data_mode';
import { styled } from '@mui/system';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// Restored module imports (ensure these files exist in your project)
import ifav from '../assets/images/ifav.png';
import warning from '../assets/images/warning_info.png';
import Map from './Summary/Map';
import Mileage from './Summary/Mileage';
import Charging_cycle from './Summary/Charging_cycles';
import DynamicTable from './Summary/Table';
import { useGraphContext } from '../GraphContext';

// Define the plot color to be used by both the strip plot and the choropleth plot
const plotColor = "#CC0000";

// ======================================================================
// Original components with styling

// Card wrapper with rounded borders (5px)
const CardWrapper = styled(Card)(({ height, backgroundColor }) => ({
  height,
  margin: '10px',
  ...(height ? {} : { flex: 1 }), // Only apply flex: 1 if no height is specified
  fontFamily: 'MAN EUROPE_Bold',
  border: '1px solid #ccc', // Added border
  borderRadius: '5px',
  boxShadow:'none',
  backgroundColor: backgroundColor || 'white', // Dynamic background color
}));

// Custom Tooltip with white background and black text
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))({
  '& .MuiTooltip-tooltip': {
    backgroundColor: 'white',
    color: 'black',
  },
  '& .MuiTooltip-arrow': {
    color: 'white',
  },
});

// Header component extended to accept an "action" prop for the info icon
const Header = styled(CardHeader)(
  ({ height, titleFontSize, titleMarginBottom, textAlign }) => ({
    color: 'black',
    padding: '4px 0px',
    fontSize: '10',
    height,
    backgroundColor: 'transparent',
    fontFamily: 'MAN EUROPE_Bold',
    borderRadius: '5px 5px 5px 5px',
    ...(textAlign && { textAlign }),
    '& .MuiCardHeader-title': {
      fontSize: '18px',
      titleFontSize,
      marginBottom: titleMarginBottom,
      marginLeft: 10,
      fontFamily: 'MAN EUROPE_Bold',
    },
  })
);

// Content component with matching rounded borders on the bottom
const Content = styled(CardContent)(({ fontSize, marginBottom }) => ({
  fontSize: 'larger',
  fontWeight: 'bold',
  textAlign: 'end',
  marginBottom,
  backgroundColor: 'transparent',
  color: 'black',
  fontFamily: 'MAN EUROPE_Bold',
  borderRadius: '5px 5px 5px 5px',
}));

// Styled component for the numeric value
const ValueText = styled('span')({
  fontSize: '1.2rem', // Adjust size as needed
  fontWeight: 'bold',
  color: 'black',
  fontFamily: 'MAN EUROPE_Bold',
});

// Styled component for the unit text (matching header style)
const UnitText = styled('span')({
  fontSize: '0.750rem',
  color: 'black',
  fontFamily: 'MAN EUROPE_regular',
  marginLeft: '0.25rem',
});

const Summary = () => {
  const { graphData } = useGraphContext();
  const {
    maps,
    mileage_vin,
    all_summary_test,
    charging_cycle,
    all_summary_test_table,
  } = graphData;

  const [accordionExpanded, setAccordionExpanded] = React.useState(false);
  const handleAccordionChange = (event, isExpanded) =>
    setAccordionExpanded(isExpanded);
    
  // Add state for the data mode (7-day or lifetime)
  const [dataMode, setDataMode] = useState('lifetime');
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const { updateGraphData } = useGraphContext();
  
  // Import the handler hook for making data requests
  const handleQuery = React.lazy(() => import('../utils/dropdown_data_loading/useHandleQuery')).default;
  
  // Set initial summary data when component loads
  useEffect(() => {
    if (all_summary_test && all_summary_test.length > 0) {
      setSummaryData(all_summary_test[0]);
    }
  }, [all_summary_test]);
  
  // Handler for toggle switch change
  const handleDataModeChange = (e) => {
    const newMode = e.target.checked ? '7day' : 'lifetime';
    
    if (dataMode !== newMode) {
      setDataMode(newMode);
      setIsLoading(true);
      
      console.log(`Data mode changed to ${newMode}. Fetching ${newMode === '7day' ? '7-day rolling average' : 'lifetime'} data.`);
      
      // Get VIN list from table data
      const vinList = all_summary_test_table.map(item => item.vin_short);
      
      // Call our API to update the summary data
      updateSummaryDataMode(vinList, newMode)
        .then(data => {
          if (data && data.length > 0) {
            setSummaryData(data[0]);
            console.log('Summary data updated:', data[0]);
          }
        })
        .catch(error => {
          console.error('Failed to update summary data:', error);
          // Revert to previous mode on error
          setDataMode(dataMode);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // Download handler for the export button
  const handleExportClick = (e, vehicleData) => {
    e.stopPropagation();

    if (!vehicleData || vehicleData.length === 0) {
      alert('No data available to export.');
      return;
    }

    const columns = Object.keys(vehicleData[0]);
    const headers = columns.join(',') + '\n';
    const rows = vehicleData
      .map((row) => columns.map((col) => `${row[col]}`).join(','))
      .join('\n');

    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'vehicle_long_list.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Define a common image style for the info icon image
  const infoImgStyle = { width: '16px', height: '16px' };

  // Define background colors based on data mode
  const getKpiBackgroundColor = () => {
    return dataMode === '7day' ? '#DBEAF6' : '#F2F2F2';
  };

  return (
    <>
      {/* Data Mode Toggle - Centered Above Main Container */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          mb: 2,
          mx: 2,
          backgroundColor: 'white',
          padding: 2,
          borderRadius: '5px',
          width: 'calc(100% - 32px)',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
          }}
        >
          {isLoading && (
            <Box sx={{ position: 'relative', display: 'inline-flex', mr: 2 }}>
              <CircularProgress 
                size={20} 
                sx={{ color: '#CC0000' }}
              />
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={dataMode === '7day'}
                onChange={handleDataModeChange}
                disabled={isLoading}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#CC0000',
                    '&:hover': {
                      backgroundColor: 'rgba(204, 0, 0, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#CC0000',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant="body1" 
                  component="span" 
                  sx={{ 
                    fontFamily: 'MAN EUROPE_Bold',
                    color: dataMode === 'lifetime' ? '#CC0000' : 'rgba(204, 0, 0, 0.4)'
                  }}
                >
                  Lifetime
                </Typography>
                <Typography 
                  variant="body2" 
                  component="span" 
                  sx={{ mx: 1, color: 'black' }}
                >
                  |
                </Typography>
                <Typography 
                  variant="body1" 
                  component="span" 
                  sx={{ 
                    fontFamily: 'MAN EUROPE_Bold',
                    color: dataMode === '7day' ? '#CC0000' : 'rgba(204, 0, 0, 0.4)'
                  }}
                >
                  7-Day
                </Typography>
              </Box>
            }
            labelPlacement="start"
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-root': {
                margin: 0,
              }
            }}
          />
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            fontStyle: 'italic',
            mt: 0.5,
            color: '#666',
            textAlign: 'center',
            width: '100%'
          }}
        >
          {dataMode === '7day' 
            ? "Showing data based on 7-day rolling average" 
            : "Showing data based on measurements since start of data acquisition"}
        </Typography>
      </Box>

      {/* Main Container - 50/50 Layout */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          backgroundColor: 'white',
          fontFamily: 'MAN EUROPE_Bold',
          borderRadius: '5px',
          margin: '10px',
          alignItems: 'flex-start',
          height: 'auto',
        }}
      >
        {/* Left Section - 50% Width - KPIs */}
        <div
          style={{
            width: '50%',
            backgroundColor: 'white',
            margin: '0px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Top Row - Total Mileage, Driving Time, Standing Time */}
          <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: '5px' }}>
            <div style={{ flex: 2 }}>
              <CardWrapper height="161px">
                <Header
                  title="Total mileage"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title="Total mileage (sum) of selected vehicle(s) since start of data acquisition"
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                />
                <Content fontSize="25px" marginBottom="0px">
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>{summaryData ? summaryData.total_mileage : all_summary_test[0].total_mileage}</ValueText>
                      <UnitText> km</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
            <div style={{ flex: 1 }}>
              <CardWrapper height="161px" backgroundColor={getKpiBackgroundColor()}>
                <Header
                  title="Ø Daily driving time"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title={dataMode === '7day' 
                        ? "Average daily total driving time of selected vehicle(s) during the past 7 calendar days" 
                        : "Average daily total driving time of selected vehicle(s) since start of data acquisition"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                  style={{ backgroundColor: 'transparent' }}
                />
                <Content
                  fontSize="25px"
                  marginBottom="0px"
                  style={{ backgroundColor: 'transparent' }}
                >
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>{summaryData ? summaryData.avg_driving_time : all_summary_test[0].avg_driving_time}</ValueText>
                      <UnitText> h</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
            <div style={{ flex: 1 }}>
              <CardWrapper height="161px" backgroundColor={getKpiBackgroundColor()}>
                <Header
                  title="Ø Daily standing time"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title={dataMode === '7day' 
                        ? "Average daily total standing time of selected vehicle(s) during the past 7 calendar days"
                        : "Average daily total standing time of selected vehicle(s) since start of data acquisition"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                  style={{ backgroundColor: 'transparent' }}
                />
                <Content
                  fontSize="25px"
                  marginBottom="0px"
                  style={{ backgroundColor: 'transparent' }}
                >
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>{summaryData ? summaryData.avg_standing_time : all_summary_test[0].avg_standing_time}</ValueText>
                      <UnitText> h</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
          </div>

          {/* Second Row - Estimated Range, Charging Power, Daily Mileage, Speed */}
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <CardWrapper height="161px" backgroundColor={getKpiBackgroundColor()}>
                <Header
                  title="Ø Estimated range"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title={dataMode === '7day' 
                        ? "Average estimated range of selected vehicle(s) during the past 7 calendar days"
                        : "Average estimated range of selected vehicle(s) since start of data acquisition"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                />
                <Content fontSize="25px" marginBottom="0px">
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>
                        {summaryData ? summaryData.avg_estimated_range : all_summary_test[0].avg_estimated_range}
                      </ValueText>
                      <UnitText> km</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
            <div style={{ flex: 1 }}>
              <CardWrapper height="161px" backgroundColor={getKpiBackgroundColor()}>
                <Header
                  title="Ø Charging power"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title={dataMode === '7day' 
                        ? "Average charging power of selected vehicle(s) during the past 7 calendar days"
                        : "Average charging power of selected vehicle(s) since start of data acquisition"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                />
                <Content fontSize="25px" marginBottom="0px">
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>
                        {summaryData ? summaryData.avg_charging_power : all_summary_test[0].avg_charging_power}
                      </ValueText>
                      <UnitText> kW</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
            <div style={{ flex: 1 }}>
              <CardWrapper height="161px" backgroundColor={getKpiBackgroundColor()}>
                <Header
                  title="Ø Daily mileage"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title={dataMode === '7day' 
                        ? "Average daily mileage of selected vehicle(s) during the past 7 calendar days"
                        : "Average daily mileage of selected vehicle(s) since start of data acquisition"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                />
                <Content fontSize="25px" marginBottom="0px">
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>
                        {summaryData ? summaryData.avg_daily_mileage : all_summary_test[0].avg_daily_mileage}
                      </ValueText>
                      <UnitText> km</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
            <div style={{ flex: 1 }}>
              <CardWrapper height="161px" backgroundColor={getKpiBackgroundColor()}>
                <Header
                  title="Ø Speed"
                  height="101px"
                  titleFontSize={17}
                  titleMarginBottom={60}
                  action={
                    <CustomTooltip
                      title={dataMode === '7day' 
                        ? "Average speed of selected vehicle(s) during the past 7 calendar days"
                        : "Average speed of selected vehicle(s) since start of data acquisition"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        style={{
                          marginRight: '5px',
                          transform: 'translateX(-5px)',
                        }}
                      >
                        <img src={ifav} alt="info icon" style={infoImgStyle} />
                      </IconButton>
                    </CustomTooltip>
                  }
                />
                <Content fontSize="25px" marginBottom="0px">
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CircularProgress size={20} sx={{ color: '#CC0000', mr: 1 }} />
                    </Box>
                  ) : (
                    <>
                      <ValueText>{summaryData ? summaryData.avg_speed : all_summary_test[0].avg_speed}</ValueText>
                      <UnitText> km/h</UnitText>
                    </>
                  )}
                </Content>
              </CardWrapper>
            </div>
          </div>
        </div>
        {/* Right Section - 50% Width - Energy Consumption */}
        <div
          style={{
            width: '50%',
            backgroundColor: 'white',
            margin: '0px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Energy Consumption BBP and CBP Charts - Aligned with KPI rows */}
          {summaryData?.technology && (
            <div style={{ marginTop: '5px', height: '380px' }}>
              {/* BBP Energy Consumption Chart */}
              {summaryData.technology === 'BBP' && (
                <CardWrapper height="380px" backgroundColor={getKpiBackgroundColor()}>
                  <Header
                    title="Energy consumption BBP"
                    height="40px"
                    titleFontSize={16}
                    titleMarginBottom={10}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            marginRight: '10px', 
                            fontFamily: 'MAN EUROPE_Bold',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }}
                        >
                          {dataMode === '7day' 
                            ? (summaryData ? summaryData.energy_cons_7dayavg : all_summary_test[0].energy_cons_7dayavg)
                            : (summaryData ? summaryData.energy_cons_lifetimeavg : all_summary_test[0].energy_cons_lifetimeavg)
                          } kWh/100km
                        </Typography>
                        <CustomTooltip
                          title="Detailed breakdown of energy consumption components for Battery Battery Pack (BBP) technology"
                          arrow
                        >
                          <IconButton
                            size="small"
                            style={{
                              marginRight: '5px',
                              transform: 'translateX(-5px)',
                            }}
                          >
                            <img src={ifav} alt="info icon" style={infoImgStyle} />
                          </IconButton>
                        </CustomTooltip>
                      </Box>
                    }
                  />
                  <Content style={{ padding: '0 10px 10px', height: '270px' }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={30} sx={{ color: '#CC0000' }} />
                      </Box>
                    ) : (
                      <div style={{ width: '100%', height: '100%' }}>
                        <Plot
                          data={[
                            {
                              type: 'bar',
                              name: 'Powertrain',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.powertrain_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.powertrain_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(75, 150, 210, 0.6)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'Lowvolt',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.bbp_lowvolt_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.bbp_lowvolt_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(172, 177, 182, 0.4)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'HVAC',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.bbp_hvac_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.bbp_hvac_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(89, 99, 109, 0.8)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'Heatpump Heater',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.heatpump_heater_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.heatpump_heater_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(89, 99, 109, 1.0)' },
                              orientation: 'h',
                            },
                          ]}
                          layout={{
                            barmode: 'stack',
                            title: '',
                            margin: { l: 40, r: 20, t: 20, b: 120 },
                            showlegend: true,
                            legend: {
                              orientation: 'h',
                              y: -0.6,
                              x: 0.5,
                              xanchor: 'center',
                            },
                            xaxis: { 
                              title: 'kWh/100km',
                              titlefont: {
                                size: 12,
                              },
                            },
                            yaxis: {
                              showticklabels: false,
                              title: '',
                            },
                          }}
                          config={{ responsive: true }}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    )}
                  </Content>
                </CardWrapper>
              )}

              {/* CBP Energy Consumption Chart */}
              {summaryData.technology === 'CBP' && (
                <CardWrapper height="342px" backgroundColor={getKpiBackgroundColor()}>
                  <Header
                    title="Energy consumption CBP"
                    height="40px"
                    titleFontSize={16}
                    titleMarginBottom={10}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            marginRight: '10px', 
                            fontFamily: 'MAN EUROPE_Bold',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }}
                        >
                          {dataMode === '7day' 
                            ? (summaryData ? summaryData.energy_cons_7dayavg : all_summary_test[0].energy_cons_7dayavg)
                            : (summaryData ? summaryData.energy_cons_lifetimeavg : all_summary_test[0].energy_cons_lifetimeavg)
                          } kWh/100km
                        </Typography>
                        <CustomTooltip
                          title="Detailed breakdown of energy consumption components for Central Battery Pack (CBP) technology"
                          arrow
                        >
                          <IconButton
                            size="small"
                            style={{
                              marginRight: '5px',
                              transform: 'translateX(-5px)',
                            }}
                          >
                            <img src={ifav} alt="info icon" style={infoImgStyle} />
                          </IconButton>
                        </CustomTooltip>
                      </Box>
                    }
                  />
                  <Content style={{ padding: '0 10px 10px', height: '270px' }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={30} sx={{ color: '#CC0000' }} />
                      </Box>
                    ) : (
                      <div style={{ width: '100%', height: '100%' }}>
                        <Plot
                          data={[
                            {
                              type: 'bar',
                              name: 'Powertrain',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.powertrain_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.powertrain_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(75, 150, 210, 0.6)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: '2nd Drive',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.cbp_2nd_drive_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.cbp_2nd_drive_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(75, 150, 210, 0.4)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'HVAC',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.cbp_hvac_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.cbp_hvac_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(89, 99, 109, 0.8)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'Cooling Heating',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.cbp_coolingheating_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.cbp_coolingheating_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(131, 138, 146, 0.6)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'Lowvolt',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.cbp_lowvolt_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.cbp_lowvolt_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(172, 177, 182, 0.4)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'ePTO',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.cbp_epto_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.cbp_epto_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(255, 205, 0, 0.6)' },
                              orientation: 'h',
                            },
                            {
                              type: 'bar',
                              name: 'Heatpump Heater',
                              y: [''],
                              x: [dataMode === '7day' 
                                ? parseFloat(summaryData?.heatpump_heater_cons_7dayavg || '0') 
                                : parseFloat(summaryData?.heatpump_heater_cons_lifetimeavg || '0')],
                              marker: { color: 'rgba(89, 99, 109, 1.0)' },
                              orientation: 'h',
                            },
                          ]}
                          layout={{
                            barmode: 'stack',
                            title: '',
                            margin: { l: 40, r: 20, t: 20, b: 140 },
                            showlegend: true,
                            legend: {
                              orientation: 'h',
                              y: -0.8,
                              x: 0.5,
                              xanchor: 'center',
                              tracegroupgap: 5,
                            },
                            xaxis: { 
                              title: 'kWh/100km',
                              titlefont: {
                                size: 12,
                              },
                            },
                            yaxis: {
                              showticklabels: false,
                              title: '',
                            },
                          }}
                          config={{ responsive: true }}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    )}
                  </Content>
                </CardWrapper>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section - Three Components Side by Side */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          backgroundColor: 'white',
          fontFamily: 'MAN EUROPE_Bold',
          borderRadius: '5px',
          margin: '10px',
          alignItems: 'stretch',
          minHeight: '400px',
          height: 'auto',
          flexWrap: 'wrap', // Allow wrapping on smaller screens
        }}
      >
        {/* Charging Cycles - Left Third */}
        <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardWrapper style={{ height: '100%', margin: '0' }}>
            <Header
              title="Total number of charging cycles"
              titleFontSize={14}
              textAlign="start"
              action={
                <CustomTooltip
                  title={
                    <>
                      The total number of charging cycles since start of data acquisition is displayed for the top 5 vehicles.
                      Additionally the average value on total number of charging cycles over all selected vehicles is displayed.
                      <br /><br />
                    </>
                  }
                  arrow
                >
                  <IconButton
                    size="small"
                    style={{
                      marginRight: '5px',
                      transform: 'translateX(-5px)',
                    }}
                  >
                    <img src={warning} alt="info icon" style={infoImgStyle} />
                  </IconButton>
                </CustomTooltip>
              }
            />
            <Content>
              <Charging_cycle value={charging_cycle} />
            </Content>
          </CardWrapper>
        </div>

        {/* Vehicle Mileage Chart - Middle Third */}
        <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardWrapper style={{ height: '100%', margin: '0' }}>
            <Header
              title="Vehicle total mileage at a glance"
              titleFontSize={14}
              textAlign="start"
              action={
                <CustomTooltip
                  title="Total mileage of each selected vehicle since start of data acquisition"
                  arrow
                >
                  <IconButton
                    size="small"
                    style={{
                      marginRight: '5px',
                      transform: 'translateX(-5px)',
                    }}
                  >
                    <img src={ifav} alt="info icon" style={infoImgStyle} />
                  </IconButton>
                </CustomTooltip>
              }
            />
            <Content style={{ padding: '10px' }}>
              <Mileage value={mileage_vin} plotColor={plotColor} />
            </Content>
          </CardWrapper>
        </div>

        {/* Fleet Distribution Map - Right Third */}
        <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
          <CardWrapper style={{ height: '100%', margin: '0' }}>
            <Header
              title="Distribution of fleet across countries"
              titleFontSize={14}
              textAlign="start"
              action={
                <CustomTooltip
                  title="Visualization of number of vehicles per sales country"
                  arrow
                >
                  <IconButton
                    size="small"
                    style={{
                      marginRight: '5px',
                      transform: 'translateX(-5px)',
                    }}
                  >
                    <img src={ifav} alt="info icon" style={infoImgStyle} />
                  </IconButton>
                </CustomTooltip>
              }
            />
            <Content style={{ padding: '10px' }}>
              <Map maps={maps} plotColor={plotColor} />
            </Content>
          </CardWrapper>
        </div>
      </div>

      {/* Accordion Section for Vehicle Long List */}
      <div
        style={{
          margin: '60px 10px 50px 10px',
          position: 'relative',
          backgroundColor: '#d3d3d3',
          borderRadius: '5px',
        }}
      >
        <Accordion
          expanded={accordionExpanded}
          onChange={handleAccordionChange}
          style={{ backgroundColor: 'rgb(248, 248, 248)', borderRadius: '5px' }}
        >
          <AccordionSummary
            aria-controls="vehicle-list-content"
            id="vehicle-list-header"
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="h6"
                style={{ marginLeft: '1.5rem', fontFamily: 'MAN EUROPE_Bold' }}
              >
                Vehicle long list
              </Typography>

              <div style={{ marginLeft: '1rem' }}>
                {accordionExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <DynamicTable data={all_summary_test_table} />
          </AccordionDetails>
        </Accordion>
        <IconButton
          onClick={(e) => handleExportClick(e, all_summary_test_table)}
          size="large"
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '5px',
            right: '25px',
            color: 'black',
          }}
        >
        <CustomTooltip
          title={
            <>

            <b>Disclaimer:</b> Data may be missing during the charging process due to provision issues. This impacts only column "TOTAL_NUMBER_OF_CHARGING_CYCLES". When data is available, all relevant KPIs are displayed as intended. The issue has been reported to the respective teams. 
          </>
          }
          arrow
        >
          <IconButton
            size="large"
            style={{
              position: 'absolute', 
              top: '5px',
              right: '65px',
              color: 'black'
            }}
          >
            <img src={warning} alt="warning icon" style={infoImgStyle} />
          </IconButton>
        </CustomTooltip>                    <DownloadIcon />
                </IconButton>
        
      </div>
    </>
  );
};

export default Summary;
